import { useMutation, useQuery } from '@apollo/client/react';
import Popover, { positionBounded } from '@components/Popover/Popover';
import { GetActorDocument } from '@components/WithUser/WithUser.graphql';
import { cardEditContext } from '@context/card-edit-context';
import { CompleteUserOnboardingTourDocument } from '@graphql/mutations/user/update-user-completed-onboarding-tours.graphql';
import { useBreakpoint } from '@hooks/use-break-point';
import { logger } from '@modules/logger';
import { useUserProfile } from '@modules/user-profile/UserProfileProvider';
import { Modal } from '@ui-lib';
import { tokens } from '@ui-lib/css';
import { TABLET_BREAKPOINT } from 'app-next/claim/card/constants';
import { type DependencyList, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import ReactJoyride, { type CallBackProps, LIFECYCLE } from 'react-joyride';
import { unresponsiveProperties } from 'ui-lib/src/lib/css/atoms/atomicProperties';

import { cardValueProviderTourSteps, templateValueProviderTourSteps } from '../CardItemEditor/utils';
import { TooltipComponent } from '../CardItemEditor/ValueProviderTours';
import { CardFlavours } from '../types';
import type { ValueProvider } from '.cache/__types__';

interface DialogRenderProps {
  show: boolean;
  updateTempValueProviderForTour?: (valueProvider: ValueProvider | null) => void;
  preventCloseOnClickOutsidePopover?: boolean;
  handleClose: () => void;
  targetNode: HTMLElement | undefined;
  positionDeps: DependencyList;
  children: ReactNode;
}

/** Responsively positions a Dialog component
 *
 * Positions a Dialog relative to the card
 * Or, positions a dialog
 */
export const ResponsiveDialog = ({
  show,
  updateTempValueProviderForTour,
  preventCloseOnClickOutsidePopover = false,
  handleClose,
  targetNode,
  positionDeps,
  children,
}: DialogRenderProps): JSX.Element => {
  const currentOverflowRef = useRef<string>(undefined);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [completeUserOnboardingTourMutation] = useMutation(CompleteUserOnboardingTourDocument, {
    refetchQueries: [
      {
        query: GetActorDocument,
        variables: {
          timezone,
        },
        errorPolicy: 'ignore', /// Ignore here since we still care about the user's partial data that contains the tour completion status
      },
    ],
  });
  const { data: actorData } = useQuery(GetActorDocument, {
    variables: {
      timezone,
    },
    fetchPolicy: 'cache-first', // Get actor query is already run when the page is loaded, just use the cache
    errorPolicy: 'ignore', /// Ignore here since we still care about the user's partial data that contains the tour completion status
    skip: false,
  });

  const userDetails = useUserProfile(false);
  const { flavour } = useContext(cardEditContext);
  const [runValueProviderTour, setRunValueProviderTour] = useState(false);

  const belowTabletBreakpoint = useBreakpoint({ type: 'max-width', breakpoint: TABLET_BREAKPOINT });
  const isTemplate = flavour === CardFlavours.TEMPLATE;

  const claimOrUpdateFlow =
    flavour === CardFlavours.CLAIM ||
    flavour === CardFlavours.ADD_DETAILS ||
    flavour === CardFlavours.INVITE_TOKEN_CLAIM;

  const teamWorkspace = userDetails?.userProfileDetails.currentWorkspace.type === 'TEAM';
  const hasCompletedOnboarding =
    teamWorkspace && userDetails?.userProfileDetails.currentWorkspace.onboardingStatus === 'COMPLETED';

  const hasNotCompletedCardValueProviderTour =
    actorData?.actor?.user?.completed_onboarding_tours?.has_completed_card_value_provider_explainer === false;

  const showCardValueProviderTour =
    hasCompletedOnboarding && !isTemplate && hasNotCompletedCardValueProviderTour;

  const hasNotCompletedTemplateValueProviderTour =
    actorData?.actor?.user?.completed_onboarding_tours?.has_completed_template_value_provider_explainer ===
    false;
  const showTemplateValueProviderTour =
    hasCompletedOnboarding && isTemplate && hasNotCompletedTemplateValueProviderTour;
  const showValueProviderTour = showCardValueProviderTour || showTemplateValueProviderTour;

  useEffect(() => {
    if (!showValueProviderTour || !show) {
      return;
    }

    /// Delay the tour until the user has been on the page for 800ms
    const timeout = setTimeout(() => {
      currentOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      setRunValueProviderTour(true);
    }, 800);

    return () => {
      clearTimeout(timeout);
    };
  }, [showValueProviderTour, show]);

  if (claimOrUpdateFlow && belowTabletBreakpoint) {
    return (
      <Modal open={show} handleClose={handleClose} position="bottom">
        <div>{children}</div>
      </Modal>
    );
  }

  async function submitAction() {
    const completedTour = isTemplate ? 'TEMPLATE_VALUE_PROVIDER_EXPLAINER' : 'CARD_VALUE_PROVIDER_EXPLAINER';
    const userId = actorData?.actor?.user?.id ?? '';
    try {
      const result = await completeUserOnboardingTourMutation({
        variables: {
          input: {
            completed_tour: completedTour,
          },
        },
      });

      const isSuccessful = result.data?.completeUserOnboardingTour?.is_success;
      if (!isSuccessful) {
        logger.error('Failed to update user card value provider tour completion', {
          completed_tour: completedTour,
          user_id: userId,
        });
      }
    } catch (error) {
      logger.error('Failed to update user card value provider tour completion', {
        error,
        completed_tour: completedTour,
        user_id: userId,
      });
    }
  }

  /**
   * Observe the joyride callback to update the currently focused value provider option
   */
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { index: step, status, lifecycle } = data;
    /// If the tour is not running or it is being initialised, don't update the value provider
    if (lifecycle !== LIFECYCLE.TOOLTIP && lifecycle !== LIFECYCLE.COMPLETE) {
      return;
    }

    const isTeamCard = !isTemplate;

    if (status === 'finished') {
      document.body.style.overflow = currentOverflowRef.current ?? 'unset';
      updateTempValueProviderForTour?.(null);
      setRunValueProviderTour(false);
      return;
    }

    if (isTemplate && step === 0) {
      updateTempValueProviderForTour?.('template');
      return;
    }

    if (isTemplate && step === 1) {
      updateTempValueProviderForTour?.('member');
      return;
    }

    if (isTeamCard && step === 0) {
      updateTempValueProviderForTour?.('admin');
      return;
    }

    if (isTeamCard && step === 1) {
      updateTempValueProviderForTour?.('member');
      return;
    }
  };

  const steps = isTemplate
    ? templateValueProviderTourSteps({ submitAction })
    : cardValueProviderTourSteps({ submitAction });

  return (
    <>
      <ReactJoyride
        disableCloseOnEsc
        disableOverlayClose
        callback={handleJoyrideCallback}
        continuous
        disableScrolling
        showProgress
        steps={steps}
        tooltipComponent={TooltipComponent}
        run={runValueProviderTour}
        spotlightPadding={17}
        styles={{
          options: {
            zIndex: unresponsiveProperties.zIndex.tooltip,
          },
          spotlight: {
            borderRadius: tokens.border.radius.xlarge,
          },
        }}
        floaterProps={{
          styles: {
            arrow: {
              base: 7,
              size: 15,
            },
          },
        }}
      />
      <Popover
        show={show}
        preventCloseOnClickOutsidePopover={preventCloseOnClickOutsidePopover || runValueProviderTour}
        onClose={handleClose}
        targetNode={targetNode}
        positioner={positionBounded({
          offsetX: 8,
          xPreference: claimOrUpdateFlow ? 'left' : 'right',
          alignY: 'cover',
          adaptive: true,
          bindToViewport: true,
        })}
        applyModalStyle={false}
        positionDeps={positionDeps}
        preventOutsideInteraction={false}
      >
        <div style={{ maxWidth: '500px', minWidth: '500px' }}>{children}</div>
      </Popover>
    </>
  );
};
